import React, { useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { db, firebaseApp } from '../firebase';
import type { Timestamp } from 'firebase/firestore';
import { User as UserIcon } from 'lucide-react';
import { cn } from '../utils';

interface PresenceAvatarsProps {
  projectId: string;
  user: User | null;
}

interface PresenceUser {
  uid: string;
  displayName: string;
  photoURL: string | null;
  lastSeen: any;
}

const DISABLE_CLIENT_FIRESTORE_PRESENCE =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

export const PresenceAvatars: React.FC<PresenceAvatarsProps> = ({ projectId, user }) => {
  const [activeUsers, setActiveUsers] = useState<PresenceUser[]>([]);

  useEffect(() => {
    if (DISABLE_CLIENT_FIRESTORE_PRESENCE) return;
    if (!user || !projectId) return;
    let interval: ReturnType<typeof setInterval> | undefined;
    let unsubscribe: (() => void) | undefined;
    let cleanupPresence: (() => void) | undefined;
    let cancelled = false;

    void import('firebase/firestore').then((firestore) => {
      if (cancelled) return;

      const { getFirestore, collection, doc, onSnapshot, setDoc, deleteDoc, serverTimestamp } = firestore;
      const realtimeDb = getFirestore(firebaseApp);

      // 1. Set presence for current user
      const presenceRef = doc(realtimeDb, 'presence', projectId, 'users', user.uid);
      const setPresence = async () => {
        try {
          await setDoc(presenceRef, {
            uid: user.uid,
            displayName: user.displayName || user.email?.split('@')[0] || 'Unknown',
            photoURL: user.photoURL || null,
            lastSeen: serverTimestamp()
          });
        } catch (e) {
          console.error("Error setting presence:", e);
        }
      };

      void setPresence();
      interval = setInterval(setPresence, 30000); // Heartbeat every 30s

      // 2. Listen for other users
      const usersRef = collection(realtimeDb, 'presence', projectId, 'users');
      unsubscribe = onSnapshot(usersRef, (snapshot) => {
        const now = Date.now();
        const users = snapshot.docs
          .map(doc => doc.data() as PresenceUser)
          .filter(u => {
            // Filter out stale users (not seen in last 2 minutes)
            if (!u.lastSeen) return true;
            const lastSeenMs = (u.lastSeen as Timestamp).toMillis();
            return (now - lastSeenMs) < 120000;
          });

        // Sort to keep current user first, then by name
        users.sort((a, b) => {
          if (a.uid === user.uid) return -1;
          if (b.uid === user.uid) return 1;
          return a.displayName.localeCompare(b.displayName);
        });

        setActiveUsers(users);
      });

      cleanupPresence = () => {
        deleteDoc(presenceRef).catch(console.error);
      };
    }).catch((error) => {
      console.error("Failed to load Firestore presence module:", error);
    });

    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
      unsubscribe?.();
      cleanupPresence?.();
    };
  }, [projectId, user]);

  if (activeUsers.length <= 1) return null;

  return (
    <div className="flex -space-x-2 overflow-hidden items-center">
      {activeUsers.map((u, i) => (
        <div 
          key={u.uid}
          className={cn(
            "inline-block h-8 w-8 rounded-full ring-2 ring-white bg-gray-100 flex items-center justify-center overflow-hidden",
            i === 0 ? "z-30" : i === 1 ? "z-20" : "z-10"
          )}
          title={u.uid === user?.uid ? `${u.displayName} (You)` : u.displayName}
        >
          {u.photoURL ? (
            <img 
              src={u.photoURL} 
              alt={u.displayName} 
              className="h-full w-full object-cover"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="text-xs font-medium text-gray-600 uppercase">
              {u.displayName.substring(0, 2)}
            </div>
          )}
          {u.uid === user?.uid && (
            <div className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-green-500 ring-1 ring-white" />
          )}
        </div>
      ))}
      <span className="ml-3 text-xs font-medium text-gray-500">
        {activeUsers.length} online
      </span>
    </div>
  );
};
